import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  notification,
  Pagination,
  theme,
  Row,
  Col,
  Transfer,
  Tooltip,
  List,
  Typography,
  Button
} from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import userAccessFormItems from 'constants/userAccessFormItems';
import CustomTitle from 'components/CustomTitle';
import CustomSelect from 'components/CustomSelect';
import { globalOp } from 'store/ducks/Global';
import GoBackButton from 'components/GoBackButton';
import { UserFunction } from 'store/ducks/Global/types';
import * as Styled from './UserAccess.styled';
import { RootState } from 'store/configureStore';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LanguageList } from 'store/ducks/globalTypes';
import languagesPage from 'constants/languages';
import i18n from 'constants/translation/i18n';

const { useToken } = theme;
const { Text } = Typography;

export const UserAccess: React.FC = () => {
  const { token } = useToken();
  const { Search } = Input;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { lang } = useSelector((state: RootState) => state.global);

  const languages = useMemo(() => languagesPage(lang), [lang]);

  const handleLangChange = (value: LanguageList) => {
    localStorage.setItem('lang', value);
    i18n.changeLanguage(value);
    dispatch(globalOp.setLang(value));
  };

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  const [form] = Form.useForm();
  const [userForms, setUserForms] = useState([0]);
  const [activeFunction, setActiveFunction] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<Record<number, Record<string, any>>>(
    {}
  );
  const [activeUsers, setActiveUsers] = useState<
    { label: string; value: number }[]
  >([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [mockData, setMockData] = useState<UserFunction[]>([]);
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([]);
  const pageSize = 1;
  const [assignKeys, setAssignKeys] = useState<React.Key[]>([]);
  const [removeKeys, setRemoveKeys] = useState<React.Key[]>([]);
  const [activity, setActivity] = useState<{ label: string; value: string }[]>(
    []
  );
  const [searchText, setSearchText] = useState('');

  const filteredUsers = users
    .filter((user) =>
      user.username.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => a.username.localeCompare(b.username));

  const handleTransferChange = (
    newTargetKeys: React.Key[],
    direction: TransferDirection,
    moveKeys: React.Key[]
  ) => {
    setTargetKeys(newTargetKeys);

    if (direction === 'right') {
      setAssignKeys((prev) => Array.from(new Set([...prev, ...moveKeys])));
      setRemoveKeys((prev) => prev.filter((key) => !moveKeys.includes(key)));
    } else if (direction === 'left') {
      setRemoveKeys((prev) => Array.from(new Set([...prev, ...moveKeys])));
      setAssignKeys((prev) => prev.filter((key) => !moveKeys.includes(key)));
    }
  };

  const addUserForm = () => {
    if (userForms.length < 20) {
      const newFormIndex = userForms.length;
      setUserForms([...userForms, newFormIndex]);
      setFormData({
        ...formData,
        [newFormIndex]: {}
      });
      setCurrentPage(newFormIndex + 1);
    } else {
      notification.warning({
        message: 'Info',
        description: t('MAX_USERS_WARNING'),
        duration: 20
      });
    }
  };

  const removeUserForm = (formIndex: number) => {
    if (userForms.length > 1) {
      const newUserForms = userForms.filter((index) => index !== formIndex);
      const newFormData = { ...formData };
      delete newFormData[formIndex];
      setUserForms(newUserForms);
      setFormData(newFormData);
      setCurrentPage((prev) => Math.min(prev, newUserForms.length));
    } else {
      notification.warning({
        message: '',
        description: t('MIN_USERS_WARNING'),
        duration: 20
      });
    }
  };

  const handleFormChange = (changedValues: any, allValues: any) => {
    const formIndex = Object.keys(changedValues)[0];
    if (formIndex) {
      setFormData({
        ...formData,
        [formIndex]: allValues[formIndex]
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchUserFunctionRoles = async () => {
    try {
      const response = await globalOp.getUserFunctionRoles(activeFunction);
      const data = response?.data || [];
      setUsers(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchUserFunctionRoles();
  }, [activeFunction]);

  const fetchActiveUsers = async () => {
    try {
      const response = await globalOp.getActiveUsers();
      const formattedOptions = response.map(
        (user: {
          first_name: string;
          id: number;
          last_name: string;
          username: string;
        }) => ({
          label: `${user.username} (${user.first_name} ${user.last_name})`,
          value: user.id
        })
      );
      setActiveUsers(formattedOptions);
    } catch (error) {}
  };

  const fetchActivity = async () => {
    try {
      const response = await globalOp.getActiveInactiveOptions();
      if (response && response.data) {
        const formattedOptions = response.data.map(
          (option: { label: string; value: boolean }) => ({
            label: option.label,
            value: option.value
          })
        );
        setActivity(formattedOptions);
      }
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error?.message ||
          'Something went wrong while fetching activity options.'
      });
    }
  };

  const fetchUserFunction = async () => {
    try {
      const params = { user_id: selectedUser };
      const response = await globalOp.getUserFunctions(params);

      const data: UserFunction[] = response?.data.map((item: any) => ({
        key: String(item.function_id),
        title: item.function_name,
        description: item.description,
        linked_to_user: item.linked_to_user
      }));

      setMockData(data);

      const defaultTargetKeys = data
        .filter((item) => item.linked_to_user)
        .map((item) => item.key);

      setTargetKeys(defaultTargetKeys);
    } catch (error) {
      console.log('Error fetching user functions:', error);
    }
  };

  const handleSubmit = async () => {
    const usersData = userForms.map((formIndex) => {
      const userFormData = formData[formIndex];
      if (!userFormData) {
        return {};
      }
      return {
        username: userFormData.username,
        password: userFormData.password,
        email: userFormData.email,
        first_name: userFormData.firstName,
        last_name: userFormData.lastName,
        deactivation_date: userFormData.deactivation_date || null
      };
    });
    try {
      const response = await globalOp.signup(usersData);

      if (response?.results?.[0]?.status === 'success') {
        notification.success({
          message: 'Info',
          description: t('USERS_CREATED'),
          duration: 20
        });
        setFormData({});
        form.resetFields();
        form.setFieldsValue(
          Object.fromEntries(
            userForms.map((index) => [
              index,
              Object.fromEntries(
                userAccessFormItems(t).map((item) => [item.name, undefined])
              )
            ])
          )
        );
        // setUserForms([]);
        await fetchUserFunctionRoles();
        fetchActiveUsers();
      } else {
        notification.error({
          message: 'Signup Failed',
          description: t('SIGNUP_FAILED'),
          duration: 10
        });
      }
    } catch (error: any) {
      console.warn('Caught in handleSubmit:', error.message);
    }
  };

  const toggleStatus = async (username: string) => {
    try {
      const response = await globalOp.userActivityStatus(username);

      const updatedUsers = await globalOp.getUserFunctionRoles(activeFunction);
      setUsers(updatedUsers?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitRoles = async () => {
    const user_id = Number(selectedUser);

    if (assignKeys.length > 0) {
      try {
        const function_ids = assignKeys.map((key) => Number(key));
        const response = await globalOp.assignUserFunctions(
          user_id,
          function_ids
        );
        fetchUserFunctionRoles();
      } catch (error: unknown) {
        console.log('Error assigning functions:', error);
      }
    }

    if (removeKeys.length > 0) {
      try {
        const function_ids = removeKeys.map((key) => Number(key));
        const response = await globalOp.removeUserFunctions(
          user_id,
          function_ids
        );
        fetchUserFunctionRoles();
      } catch (error: unknown) {
        console.log('Error removing functions:', error);
      }
    }

    setAssignKeys([]);
    setRemoveKeys([]);
  };

  useEffect(() => {
    fetchActiveUsers();
    fetchActivity();
  }, []);

  useEffect(() => {
    fetchUserFunction();
  }, [selectedUser]);

  return (
    <Styled.StyledContainer token={token}>
      <Styled.HeaderRow>
        <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
        <CustomSelect
          size='md'
          options={languages}
          value={lang}
          onChange={handleLangChange}
        />
      </Styled.HeaderRow>
      <Row justify='center'>
        <Col xs={24} sm={8} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('ADD_NEW_USERS')} level={3} />
            <Row>
              <Col>
                <Styled.StyledButton
                  token={token}
                  type='primary'
                  size='large'
                  onClick={addUserForm}
                >
                  {t('ADD_USER')}
                </Styled.StyledButton>
              </Col>
              <Col>
                <Pagination
                  current={currentPage}
                  total={userForms.length}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                />
              </Col>
            </Row>
            <Form
              form={form}
              name='userForm'
              layout='vertical'
              onFinish={handleSubmit}
              autoComplete='off'
              onValuesChange={handleFormChange}
            >
              {userForms
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((formIndex, index) => (
                  <Styled.InputForm key={formIndex}>
                    <CustomTitle
                      title={`${t('USER')} ${formIndex + 1}`}
                      level={4}
                    />
                    {userAccessFormItems(t).map((item) => (
                      <Styled.StyledFormItem
                        token={token}
                        key={`${item.name}_${formIndex}`}
                        label={item.label}
                        name={[formIndex, item.name]}
                        dependencies={
                          item.name === 'confirmPassword'
                            ? [[formIndex, 'password']]
                            : undefined
                        }
                        rules={
                          item.name === 'confirmPassword'
                            ? [
                                {
                                  required: true,
                                  message: 'Please confirm your password'
                                },
                                {
                                  validator: (_, value) => {
                                    if (
                                      !value ||
                                      form.getFieldValue([
                                        formIndex,
                                        'password'
                                      ]) === value
                                    ) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error('Passwords do not match')
                                    );
                                  }
                                }
                              ]
                            : item.rules
                        }
                      >
                        {item.type === 'password' ? (
                          <Input.Password visibilityToggle />
                        ) : item.type === 'custom' && item.render ? (
                          item.render()
                        ) : (
                          <Input type={item.type} />
                        )}
                      </Styled.StyledFormItem>
                    ))}

                    <Styled.StyledButton
                      token={token}
                      type='primary'
                      size='large'
                      onClick={() => removeUserForm(formIndex)}
                    >
                      {t('REMOVE_USER')}
                    </Styled.StyledButton>
                  </Styled.InputForm>
                ))}
              <Row>
                <Col>
                  <Styled.StyledButton
                    token={token}
                    type='primary'
                    size='large'
                    htmlType='submit'
                  >
                    {t('SAVE')}
                  </Styled.StyledButton>
                </Col>
              </Row>
            </Form>
          </Styled.FormContainer>
        </Col>
        <Col xs={24} sm={16} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('MANAGE_ACCESS')} level={3} />
            <Styled.CustomWrapper>
              <CustomSelect
                options={activeUsers}
                value={selectedUser}
                onChange={(value: React.SetStateAction<number | null>) =>
                  setSelectedUser(value)
                }
                placeholder={t('SELECT_USER')}
                filterOption={(input: string, option: { label: string }) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
              {selectedUser && (
                <Tooltip title={t('CLEAR_SELECTION')}>
                  <Styled.CloseCircleOutlinedIcon
                    onClick={() => setSelectedUser(null)}
                  />
                </Tooltip>
              )}
            </Styled.CustomWrapper>

            <Styled.ActiveUsers>
              {selectedUser && (
                <>
                  <Transfer
                    dataSource={mockData}
                    titles={[t('NOT_ACTIVE_FUNCTIONS'), t('ACTIVE_FUNCTIONS')]}
                    targetKeys={targetKeys}
                    onChange={handleTransferChange}
                    render={(item) => item.title}
                    locale={{ itemUnit: '', itemsUnit: '' }}
                  />
                  <Styled.SubmitContainer>
                    <Styled.StyledButton
                      token={token}
                      type='primary'
                      onClick={handleSubmitRoles}
                      disabled={
                        assignKeys.length === 0 && removeKeys.length === 0
                      }
                    >
                      {t('SAVE')}
                    </Styled.StyledButton>
                  </Styled.SubmitContainer>
                </>
              )}
            </Styled.ActiveUsers>
          </Styled.FormContainer>
        </Col>
        <Col xs={24} sm={12} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('VIEW_USER_RIGHTS')} level={5} />
            <CustomSelect
              options={activity}
              value={activeFunction}
              onChange={(value: boolean) => setActiveFunction(value)}
              placeholder={t('SELECT_STATUS')}
            />
            <Search
              placeholder={t('SEARCH_USER')}
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Styled.ViewUsersContainer>
              <List
                itemLayout='horizontal'
                dataSource={filteredUsers}
                bordered
                renderItem={(user) => {
                  const functionNames =
                    user.functions?.map(
                      (func: { function_name: string }) => func.function_name
                    ) || [];
                  const roleNames =
                    user.functions?.flatMap((func: { roles: any[] }) =>
                      func.roles.map((role) => role.role_name)
                    ) || [];
                  return (
                    <List.Item>
                      <List.Item.Meta
                        title={<Text strong>{user.username}</Text>}
                        description={
                          <>
                            <Styled.UsersData>
                              <Text>{user.email}</Text>
                              <Text>
                                {functionNames.length > 1
                                  ? t('FUNCTION_NAMES')
                                  : t('FUNCTION_NAME')}
                                : {functionNames.join(', ')}
                              </Text>
                            </Styled.UsersData>
                            <Styled.UsersData>
                              <Text type='secondary'>
                                {roleNames.length > 1 ? t('ROLES') : t('ROLE')}:{' '}
                                {roleNames.join(', ')}
                              </Text>
                            </Styled.UsersData>
                            <Button
                              type='primary'
                              danger={user.active}
                              onClick={() => toggleStatus(user.username)}
                            >
                              {activeFunction
                                ? t('INACTIVATE_USER')
                                : t('ACTIVATE_USER')}
                            </Button>
                          </>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Styled.ViewUsersContainer>
          </Styled.FormContainer>
        </Col>
      </Row>
    </Styled.StyledContainer>
  );
};

export default UserAccess;
